import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Trash2 } from 'lucide-react';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Skeleton } from '../../../components/ui/Skeleton.jsx';
import { customerDraftsApi } from '../../../api/customerDrafts.js';
import { fmt } from '../../../lib/formatters.js';
import { t } from '../../../lib/i18n.js';

function previewLabel(d) {
  const name = [d.fields?.firstName, d.fields?.lastName].filter(Boolean).join(' ').trim();
  if (name) return name;
  const text = (d.text || '').trim();
  return text ? (text.length > 80 ? `${text.slice(0, 80)}...` : text) : 'Bozza senza testo';
}

export function CustomerDraftsModal({ open, onClose, onResume }) {
  const queryClient = useQueryClient();

  const draftsQuery = useQuery({
    queryKey: ['customer-drafts', 'list'],
    enabled: open,
    queryFn: () => customerDraftsApi.list({ limit: 50 })
  });
  const items = draftsQuery.data?.items || [];

  const deleteMutation = useMutation({
    mutationFn: (id) => customerDraftsApi.remove(id),
    onSuccess: () => {
      toast.success('Bozza eliminata.');
      queryClient.invalidateQueries({ queryKey: ['customer-drafts'] });
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  return (
    <Modal open={open} onClose={onClose} title="Bozze clienti" size="md">
      <div className="flex flex-col gap-2">
        {draftsQuery.isLoading ? (
          [0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)
        ) : items.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-muted">Nessuna bozza salvata.</p>
        ) : (
          items.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-surface px-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm text-ink">{previewLabel(d)}</p>
                <p className="text-xs text-ink-faint">Aggiornata {fmt.dateTime(d.updatedAt)}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button size="sm" variant="secondary" onClick={() => onResume(d)}>Riprendi</Button>
                <button
                  type="button"
                  className="text-ink-faint hover:text-danger disabled:opacity-50"
                  title="Elimina bozza"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate(d.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}
